import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';

import { selectAccountCompletionState } from '../../../state/auth/auth.selectors';
import { selectLang } from '../../../state';
import { completePendingFreePlanRegistration, savePendingFreePlanWheel, savePendingFreePlanWheelFailure } from '../../../state/actions';
import { CompleteSignupFormComponent } from '../../user-profile/features/account-profile/features/complete-signup-form/complete-signup-form.component';

@Component({
  selector: 'wil-free-plan-registration-modal-content',
  standalone: true,
  imports: [CompleteSignupFormComponent, MatProgressSpinnerModule, MatIcon],
  templateUrl: './free-plan-registration-modal-content.component.html',
  styleUrl: './free-plan-registration-modal-content.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FreePlanRegistrationModalContentComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly actions$ = inject(Actions);
  private readonly dialogRef = inject(MatDialogRef<unknown>);

  public readonly navigateToLogin = output<void>();
  public readonly subheading = input('No credit card required');
  public readonly isCreating = signal(false);
  public readonly hasError = signal(false);
  private saveStarted = false;

  public ngOnInit(): void {
    this.store
      .select(selectAccountCompletionState)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ status }) => {
        if (status === 'failure') {
          this.isCreating.set(false);
        }
      });

    this.actions$
      .pipe(ofType(completePendingFreePlanRegistration, savePendingFreePlanWheelFailure), takeUntilDestroyed(this.destroyRef))
      .subscribe((action) => {
        if (!this.saveStarted) return;

        if (action.type === completePendingFreePlanRegistration.type) {
          this.dialogRef.close({ success: true });
          return;
        }

        this.isCreating.set(false);
        this.hasError.set(true);
      });
  }

  public onRegistrationStarted(): void {
    this.isCreating.set(true);
  }

  public onRegistrationSucceeded(): void {
    if (this.saveStarted) return;

    this.saveStarted = true;
    this.store
      .select(selectLang)
      .pipe(take(1))
      .subscribe((locale) => this.store.dispatch(savePendingFreePlanWheel({ locale })));
  }
}
